import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useLocale } from './LocaleProvider';
import { translationAvailable } from './availability';
import { arrivedFromOtherLanguageSite, counterpartOrigin, LANGUAGE_DISMISSED_KEY, LANGUAGE_PREFERENCE_KEY, localizePath, preferredLanguage, readPreference, SITE_LOCALE, SITE_ORIGINS, type Locale, savePreference } from './locale';

const OTHER_LOCALE: Locale = SITE_LOCALE === 'en' ? 'pt-BR' : 'en';

/**
 * The other language lives on the other domain. The prerendered link points at the live site; after
 * hydration it moves to this host's own counterpart (dev preview, localhost), so the HTML still matches.
 */
function useOtherSite(): string {
  const [origin, setOrigin] = useState(SITE_ORIGINS[OTHER_LOCALE]);
  useEffect(() => { setOrigin(counterpartOrigin(window.location, OTHER_LOCALE)); }, []);
  return origin;
}
const otherSiteHref = (origin: string, current: string) => origin + localizePath(current, SITE_LOCALE);

const LABELS: Record<Locale, { name: string; code: string }> = {
  en: { name: 'English', code: 'EN' },
  'pt-BR': { name: 'Português (Brasil)', code: 'PT' },
};

export function LanguageSelector() {
  const location = useLocation();
  const { locale } = useLocale();
  const otherOrigin = useOtherSite();
  const current = location.pathname + location.search + location.hash;
  const portugueseAvailable = translationAvailable(location.pathname);
  const label = locale === 'en' ? 'Website language' : 'Idioma do site';
  const option = (target: Locale) => {
    const { name } = LABELS[target];
    const selected = target === SITE_LOCALE;
    const body = <><span className="flex-1">{name}</span>{selected && <Check className="h-4 w-4" aria-hidden="true" />}</>;
    if (target === 'pt-BR' && !portugueseAvailable) return <DropdownMenuItem key={target} disabled lang={target} className="flex-col items-start gap-0">
      <span>{name}</span><span className="text-xs">indisponível nesta página</span>
    </DropdownMenuItem>;
    return <DropdownMenuItem asChild key={target} className="gap-3">
      {selected
        ? <Link to={localizePath(current, target)} lang={target} hrefLang={target} aria-current="true">{body}</Link>
        : <a href={otherSiteHref(otherOrigin, current)} lang={target} hrefLang={target}
            onClick={() => savePreference(LANGUAGE_PREFERENCE_KEY, target)}>{body}</a>}
    </DropdownMenuItem>;
  };
  return <DropdownMenu>
    <DropdownMenuTrigger aria-label={`${label}: ${LABELS[SITE_LOCALE].name}`}
      className="flex items-center gap-1 rounded px-1 py-2 text-sm text-muted-foreground transition-colors duration-200 hover:text-primary focus:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      <Globe className="h-4 w-4" aria-hidden="true" />
      <span>{LABELS[SITE_LOCALE].code}</span>
      <ChevronDown className="h-4 w-4" aria-hidden="true" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" aria-label={label} className="min-w-48">
      {option('en')}
      {option('pt-BR')}
    </DropdownMenuContent>
  </DropdownMenu>;
}

export function LanguageSuggestion() {
  const { locale } = useLocale();
  const location = useLocation();
  const otherOrigin = useOtherSite();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // Arriving from the other language's site means the visitor just chose this language there
    // (localStorage doesn't cross domains), so remember it here instead of asking again.
    const fromOtherSite = arrivedFromOtherLanguageSite();
    if (fromOtherSite) savePreference(LANGUAGE_PREFERENCE_KEY, SITE_LOCALE);
    setVisible(!fromOtherSite && locale === 'en' && translationAvailable(location.pathname) && !readPreference(LANGUAGE_PREFERENCE_KEY) &&
      !readPreference(LANGUAGE_DISMISSED_KEY) && preferredLanguage(navigator.languages || [navigator.language]) === 'pt-BR');
  }, [locale, location.pathname]);
  if (!visible || locale !== 'en') return null;
  return <aside lang="pt-BR" aria-label="Sugestão de idioma" className="fixed bottom-4 left-4 z-[60] max-w-[calc(100vw-2rem)] rounded-lg border bg-card p-4 text-foreground shadow-lg">
    <p className="mb-3">Prefere navegar em português do Brasil?</p>
    <div className="flex flex-wrap gap-4">
      <a className="font-semibold underline" href={otherSiteHref(otherOrigin, location.pathname + location.search + location.hash)}
        onClick={() => { savePreference(LANGUAGE_PREFERENCE_KEY, 'pt-BR'); setVisible(false); }}>Ver em português</a>
      <button type="button" className="underline" onClick={() => { savePreference(LANGUAGE_DISMISSED_KEY, '1'); setVisible(false); }}>Agora não</button>
    </div>
  </aside>;
}
